import { clsx } from 'clsx';
import { DataCard, ScoreRing, SkeletonCard, QueryError, EmptyState } from '../../../../components/ui';

/** Below this the API is mostly returning its "no data = 50" neutral defaults
 *  (confidence = connected sources / 10). Rendering a 50 as a health score is
 *  a fabricated number, so we show what is missing instead. */
const MIN_CONFIDENCE = 0.5;

export interface BusinessPulseWidgetProps {
  nerveCenter: { data?: any; isLoading: boolean; isError: boolean; error: unknown; refetch: () => void };
}

export function BusinessPulseWidget({ nerveCenter }: BusinessPulseWidgetProps) {
  const nc = nerveCenter.data;
  const health = nc?.overall_health ?? 0;
  const confident = (nc?.confidence ?? 0) >= MIN_CONFIDENCE;
  // The API states only an absolute 'critical' (no prior state exists to compare with), never a direction.
  const isCritical = nc?.trend === 'critical';
  const dims = nc?.dimensions;
  const dimList: Array<{ label: string; value: number; key: string }> = dims ? [
    { label: 'Revenue', value: dims.revenue_health, key: 'rev' },
    { label: 'Clients', value: dims.client_health, key: 'cli' },
    { label: 'Pipeline', value: dims.pipeline_health, key: 'pip' },
    { label: 'Relationships', value: dims.relationship_health, key: 'rel' },
    { label: 'Operations', value: dims.operational_health, key: 'ops' },
    { label: 'Cash', value: dims.cash_health, key: 'cash' },
  ] : [];
  const topSignals = nc?.priority_signals?.slice(0, 3) ?? [];

  return (
    <DataCard
      title="Business Pulse"
      badge={nc && confident && isCritical ? <span className="text-2xs font-semibold text-danger">⚠ Critical</span> : undefined}
    >
      {nerveCenter.isLoading ? <SkeletonCard /> : nerveCenter.isError ? <QueryError error={nerveCenter.error} onRetry={() => nerveCenter.refetch()} compact /> : !nc ? (
        <p className="text-2xs text-ink-dim italic">Intelligence engine warming up…</p>
      ) : !confident ? (
        <EmptyState
          compact
          icon="chart"
          title="Not enough connected data to score"
          description={`Scoring confidence is ${Math.round((nc.confidence ?? 0) * 100)}%. Connect a CRM and a billing source in Settings → Integrations to unlock business health.`}
        />
      ) : (
        <div className="space-y-4">
          {/* Health ring + score */}
          <div className="flex items-center gap-4">
            <ScoreRing score={health} size={56} strokeWidth={4} colorScale="health" />
            <div>
              <p className="text-2xl font-semibold font-mono tabular-nums text-ink">{health}</p>
              <p className="text-2xs text-ink-dim">Overall health</p>
            </div>
          </div>

          {/* Dimension bars */}
          <div className="space-y-1.5">
            {dimList.map(d => (
              <div key={d.key} className="flex items-center gap-2">
                <span className="w-20 text-2xs text-ink-dim truncate">{d.label}</span>
                <div className="flex-1 h-1.5 rounded-full bg-surface-2 overflow-hidden">
                  <div
                    className={clsx('h-full rounded-full transition-all', d.value >= 70 ? 'bg-success' : d.value >= 40 ? 'bg-warn' : 'bg-danger')}
                    style={{ width: `${Math.min(d.value, 100)}%` }}
                  />
                </div>
                <span className="w-7 text-right text-2xs font-mono tabular-nums text-ink-muted">{Math.round(d.value)}</span>
              </div>
            ))}
          </div>

          {/* Top signals */}
          {topSignals.length > 0 && (
            <div className="space-y-1.5 pt-1 border-t border-border">
              <p className="text-2xs font-semibold uppercase tracking-wider text-ink-dim">Top Signals</p>
              {topSignals.map((s: any, i: number) => (
                <div key={i} className="flex items-start gap-2 text-2xs">
                  <span className={clsx('mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full', s.urgency === 'immediate' ? 'bg-danger' : s.urgency === 'today' ? 'bg-warn' : 'bg-ink-dim/40')} />
                  <div className="min-w-0 flex-1">
                    <p className="text-ink leading-snug">{s.entity}: {s.recommended_action}</p>
                    {s.revenue_impact_cents !== 0 && (
                      <p className="text-ink-dim font-mono">${Math.abs(Math.round(s.revenue_impact_cents / 100)).toLocaleString()} {s.revenue_impact_cents < 0 ? 'at risk' : 'opportunity'}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </DataCard>
  );
}
